import {Trans} from '@lingui/macro';
import React, {FC, RefObject} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {PreviewReposition} from '../../../../libs/shared/ui/src/components/reposition/previewReposition';
import {shadows} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {VisualRecipientMap} from '../../../helpers/models/recipientHelpers';
import {LoadedMessageModel, MessageVisualTypesEnum} from '../../../models/api/messageModel';
import {openMessageRenderPopup} from '../../../services/popups';
import {ContactOrAccountDetailsPanelId, NewPanelId} from '../../../store/panels/panelStateBase';
import {AsyncLoader} from '../../common/loaders/asyncLoader';
import {MessageViewerChatBody} from '../../inboxes/viewer/activity/message/chat/messageViewerChatBody';
import {MessageViewerEmailBody} from '../../inboxes/viewer/activity/message/email/messageViewerEmailBody';
import {MessageViewerAvatar} from '../../inboxes/viewer/activity/message/messageViewerAvatar';
import {MessageViewerDetails} from '../../inboxes/viewer/activity/message/messageViewerDetails';
import {MessageViewerTweetBody} from '../../inboxes/viewer/activity/message/tweet/messageViewerTweetBody';
import {ConversationPreviewSkeleton} from './skeleton/conversationPreviewSkeleton';

/*
 * Types.
 */

export interface ConversationPreviewProps {
  panelId: ContactOrAccountDetailsPanelId;
  conversationId: number;
  anchor: RefObject<HTMLElement>;
  viewerConversationId: number | undefined;
  message: LoadedMessageModel | undefined;
  recipients: VisualRecipientMap;
  asyncStatus: AsyncStatusesEnum;
  onRetryClick: Handler;
  onOpenConversationClick: Handler;
}

/*
 * Style.
 */

const StyledPreviewDiv = styled.div`
  overflow: auto;
  border-radius: 16px;
  background-color: ${(p) => p.theme.greys.white};
  ${shadows.dialogsAndDropdowns};

  width: 360px;
  max-height: 350px;
`;

const StyledCurrentlyViewingDiv = styled.div`
  border-radius: 20px;
  background-color: ${(p) => p.theme.greys.white};
  ${shadows.dialogsAndDropdowns};
  padding: 8px 10px;
  margin: 10px;
  color: ${(p) => p.theme.greys.shade80};
  font-style: italic;
`;

const StyledDefaultPaddingDiv = styled.div`
  padding: 16px;
`;

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template-areas: 'avatar details button';
  grid-template-columns: min-content auto min-content;
  padding: 8px;
`;

const StyledHeaderDetailsDiv = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  max-width: 250px;
`;

const StyledButtonDiv = styled.div`
  grid-area: button;
`;

const StyledMessageDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.alphas.black20};
`;

/*
 * Component.
 */

export const ConversationPreview: FC<ConversationPreviewProps> = (props) => {
  const {panelId, asyncStatus, conversationId, onRetryClick, viewerConversationId} = props;

  if (conversationId === viewerConversationId) {
    return (
      <PreviewReposition anchor={props.anchor} popperPlacement="left" shouldHideArrow>
        <StyledCurrentlyViewingDiv>
          <Trans id="conversation-preview-currently-viewing">Currently viewing</Trans>
        </StyledCurrentlyViewingDiv>
      </PreviewReposition>
    );
  }

  return (
    <PreviewReposition anchor={props.anchor} popperPlacement="left">
      <StyledPreviewDiv {...buildInteractionIdProps(`conversation-preview-${conversationId}`)}>
        {renderContent(props, asyncStatus, panelId, onRetryClick)}
      </StyledPreviewDiv>
    </PreviewReposition>
  );
};

function renderContent(
  props: ConversationPreviewProps,
  asyncStatus: AsyncStatusesEnum | undefined,
  panelId: NewPanelId,
  onRetryClick: Handler,
) {
  const {message} = props;
  if (asyncStatus === AsyncStatusesEnum.LOADING) {
    return <ConversationPreviewSkeleton />;
  }

  if (asyncStatus === AsyncStatusesEnum.FAILED || !message) {
    return (
      <StyledDefaultPaddingDiv>
        <AsyncLoader
          size={VisualSizesEnum.LARGE}
          status={AsyncStatusesEnum.FAILED}
          error={<Trans id="conversation-preview-error">Something went wrong.</Trans>}
          onRetryClick={onRetryClick}
        />
      </StyledDefaultPaddingDiv>
    );
  }

  return (
    <>
      {renderHeader(message, props)}
      <StyledMessageDiv>{renderMessageBody(message, panelId)}</StyledMessageDiv>
    </>
  );
}

function renderHeader(message: LoadedMessageModel, props: ConversationPreviewProps) {
  const {recipients, panelId, onOpenConversationClick} = props;
  return (
    <StyledHeaderDiv>
      <MessageViewerAvatar panelId={panelId} message={message} visualRecipients={recipients} />
      <StyledHeaderDetailsDiv>
        <MessageViewerDetails
          panelId={panelId}
          message={message}
          visualRecipients={recipients}
          activitySourceVisualType={undefined}
          isMessageViewerExpanded
          shouldShowSubjectWhenCollapsed
        />
      </StyledHeaderDetailsDiv>
      <StyledButtonDiv>
        <IconButton
          iconName="popout"
          label={<Trans id="conversation-preview-view-in-external-window">View in external window</Trans>}
          onClick={onOpenConversationClick}
        />
      </StyledButtonDiv>
    </StyledHeaderDiv>
  );
}

function renderMessageBody(message: LoadedMessageModel, panelId: NewPanelId) {
  switch (message.visualType) {
    case MessageVisualTypesEnum.EMAIL:
      return (
        <MessageViewerEmailBody
          message={message}
          isUserVisitor={false}
          panelId={panelId}
          hasMessageAnnotations={false}
          isExternalContentHidden={false}
          onOpenMessageRenderClick={() => openMessageRenderPopup(message)}
        />
      );
    case MessageVisualTypesEnum.TWEET:
      return <MessageViewerTweetBody message={message} panelId={panelId} hasMessageAnnotations={false} />;
    case MessageVisualTypesEnum.CHAT:
      return (
        <MessageViewerChatBody
          message={message}
          isUserVisitor={false}
          panelId={panelId}
          hasMessageAnnotations={false}
        />
      );
    default:
      return null;
  }
}
